import { useStore } from '@hooks/useStore'
import { Close, Email, ExpandMore, ExpandLess } from '@mui/icons-material'
import {
  Box,
  Chip,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
} from '@mui/material'
import { IEmailLogWithRelations } from '@typings'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import dayjs from 'dayjs'

interface EmailLogsDialogProps {
  open: boolean
  onClose: () => void
}

export const EmailLogsDialog = observer(({ open, onClose }: EmailLogsDialogProps) => {
  const { email } = useStore()
  const [expandedRow, setExpandedRow] = useState<number | null>(null)

  useEffect(() => {
    if (open) {
      email.getLogs({})
      setExpandedRow(null)
    }
  }, [open])

  const handleRowClick = (logId: number) => {
    setExpandedRow(expandedRow === logId ? null : logId)
  }

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      pending: 'In behandeling',
      sent: 'Verzonden',
      delivered: 'Afgeleverd',
      failed: 'Mislukt',
      bounced: 'Afgewezen',
      opened: 'Geopend',
      complained: 'Spam gemeld',
    }
    return labels[status] || status
  }

  const getStatusColor = (status: string): 'default' | 'warning' | 'success' | 'error' | 'info' => {
    const colors: Record<string, 'default' | 'warning' | 'success' | 'error' | 'info'> = {
      pending: 'warning',
      sent: 'success',
      delivered: 'success',
      failed: 'error',
      bounced: 'error',
      opened: 'info',
      complained: 'error',
    }
    return colors[status] || 'default'
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth>
      <DialogTitle>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center">
          <Stack
            direction="row"
            spacing={1}
            alignItems="center">
            <Email />
            <Typography variant="h6">E-mail Logboek</Typography>
          </Stack>
          <IconButton
            onClick={onClose}
            size="small">
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent>
        {email.loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography>Laden...</Typography>
          </Box>
        ) : !email.logs_list || email.logs_list.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">Geen e-mails gevonden</Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 1 }}>
              E-mails verzonden via het systeem worden hier getoond
            </Typography>
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: 500, overflowX: 'auto' }}>
            <Table
              size="small"
              sx={{
                minWidth: 650,
                width: '100%',
              }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 50 }} />
                  <TableCell sx={{ minWidth: 150 }}>Datum</TableCell>
                  <TableCell sx={{ minWidth: 200 }}>Ontvanger</TableCell>
                  <TableCell sx={{ minWidth: 250 }}>Onderwerp</TableCell>
                  <TableCell sx={{ minWidth: 100 }}>Document</TableCell>
                  <TableCell sx={{ minWidth: 130 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {email.logs_list?.map((log: IEmailLogWithRelations) => (
                  <>
                    <TableRow
                      key={log.id}
                      hover
                      onClick={() => handleRowClick(log.id)}
                      sx={{ cursor: 'pointer' }}>
                      <TableCell>
                        <IconButton size="small">
                          {expandedRow === log.id ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {log.sent_at
                            ? dayjs(log.sent_at).format('DD-MM-YYYY HH:mm')
                            : dayjs(log.created_at).format('DD-MM-YYYY HH:mm')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontWeight="medium">
                          {log.recipient_name || log.recipient_email}
                        </Typography>
                        {log.recipient_name && (
                          <Typography
                            variant="caption"
                            color="text.secondary">
                            {log.recipient_email}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color="text.secondary">
                          {log.subject}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {log.document_id ? (
                          <Typography
                            variant="body2"
                            color="text.secondary">
                            #{log.document_id}
                          </Typography>
                        ) : (
                          <Typography
                            variant="body2"
                            color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(log.status)}
                          color={getStatusColor(log.status)}
                          size="small"
                        />
                        {log.error_message && (
                          <Typography
                            variant="caption"
                            color="error"
                            display="block"
                            sx={{ mt: 0.5 }}>
                            {log.error_message}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow key={`${log.id}-detail`}>
                      <TableCell
                        style={{ paddingBottom: 0, paddingTop: 0 }}
                        colSpan={6}>
                        <Collapse
                          in={expandedRow === log.id}
                          timeout="auto"
                          unmountOnExit>
                          <Box sx={{ margin: 2 }}>
                            <Typography
                              variant="subtitle2"
                              gutterBottom
                              component="div">
                              E-mail inhoud
                            </Typography>
                            <Paper
                              variant="outlined"
                              sx={{
                                p: 2,
                                backgroundColor: 'background.default',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                              }}>
                              <Typography
                                variant="body2"
                                component="div"
                                sx={{ fontFamily: 'monospace' }}>
                                {log.body}
                              </Typography>
                            </Paper>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  )
})
